import React, { useEffect, useState, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, CalendarDays, Mail, Phone, MapPin, X, RotateCcw, ClipboardList, LogIn, Navigation, Globe, Instagram, Facebook, Tag, ChevronRight, MessageSquare } from 'lucide-react';
import { useSettings } from './SettingsContext';
import { useBranches, BranchOrderMethods, Branch } from './BranchesContext';
import { BranchStatusBadge } from './BranchStatus';
import { useCustomerAuth } from './CustomerAuthContext';
import { useCart } from './CartContext';
import { useOrders } from './OrdersContext';
import { usePromotions, Promotion } from './PromotionsContext';
import { useScreenInit } from '../useScreenInit.js';
type SheetMode = 'menu' | 'reserve' | 'contact' | null;
const LAST_LOCATION_KEY = 'mp_last_location';
const mapsUrl = (q: string) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
const LANGUAGES: {
  value: 'en' | 'am' | 'or';
  label: string;
}[] = [{
  value: 'en',
  label: 'English'
}, {
  value: 'am',
  label: 'አማርኛ'
}, {
  value: 'or',
  label: 'Afaan Oromoo'
}];
const CURRENCIES: {
  value: 'ETB' | 'USD' | 'EUR' | 'GBP';
  label: string;
}[] = [{
  value: 'ETB',
  label: 'ETB (Br)'
}, {
  value: 'USD',
  label: 'USD ($)'
}, {
  value: 'EUR',
  label: 'EUR (€)'
}, {
  value: 'GBP',
  label: 'GBP (£)'
}];
export function CustomerHome() {
  const {
    general,
    updateGeneral
  } = useSettings();
  const {
    branches,
    setBranchId
  } = useBranches();
  const navigate = useNavigate();
  const init = useScreenInit() || {};
  const [sheet, setSheet] = useState<SheetMode>(init.sheet ?? null);
  const [prefsOpen, setPrefsOpen] = useState(false);
  const activeBranches = branches.filter(b => b.active);
  const locationList = activeBranches.length ? activeBranches : branches;
  const reservationLocations = locationList.filter(b => b.reservationsEnabled);
  const reservationsAvailable = reservationLocations.length > 0;
  const lastLocationId = typeof window !== 'undefined' ? window.localStorage.getItem(LAST_LOCATION_KEY) : null;
  const goToBranch = (id: string, mode: 'menu' | 'reserve') => {
    setBranchId(id);
    try {
      window.localStorage.setItem(LAST_LOCATION_KEY, id);
    } catch {
      /* ignore */
    }
    setSheet(null);
    navigate(`/menu?branch=${id}${mode === 'reserve' ? '&intent=reserve' : ''}`);
  };
  // Skip the picker entirely when there's only one eligible location.
  const startFlow = (mode: 'menu' | 'reserve') => {
    const list = mode === 'reserve' ? reservationLocations : locationList;
    if (list.length === 1) {
      goToBranch(list[0].id, mode);
    } else {
      setSheet(mode);
    }
  };
  const handleSelectLocation = (id: string, mode: 'menu' | 'reserve') => goToBranch(id, mode);
  // Returning-customer recognition. `authPreview` (from Screens) can force the
  // guest state so it can be previewed regardless of the real auth session.
  const {
    isAuthenticated,
    customer
  } = useCustomerAuth();
  const authed = init.authPreview === 'guest' ? false : isAuthenticated;
  const {
    orders
  } = useOrders();
  const {
    clear,
    addItem
  } = useCart();
  const {
    promotions
  } = usePromotions();
  const activePromos = promotions.filter(p => p.active && (!p.expiresAt || p.expiresAt > Date.now()));
  const greetingName = customer?.firstName || customer?.name?.split(' ')[0] || 'there';
  const lastOrder = isAuthenticated && customer?.phone ? [...orders].filter(o => o.customerPhone === customer.phone).sort((a, b) => b.placedAt - a.placedAt)[0] : undefined;
  const reorderLast = () => {
    if (!lastOrder) return;
    clear();
    if (lastOrder.branchId) {
      setBranchId(lastOrder.branchId);
      try {
        window.localStorage.setItem(LAST_LOCATION_KEY, lastOrder.branchId);
      } catch {
        /* ignore */
      }
    }
    lastOrder.items.forEach(it => {
      // Carry modifiers for display only (price 0) so the line total matches
      // the original order's unit price rather than double-counting.
      const mods = (it.modifiers || []).map((m, i) => ({
        id: `${it.name}-${m.name}-${i}`,
        name: m.name,
        price: 0
      }));
      const name = it.variation ? `${it.name} (${it.variation})` : it.name;
      for (let i = 0; i < (it.quantity || 1); i++) {
        addItem({
          itemId: it.id || it.name,
          name,
          basePrice: it.unitPrice,
          modifiers: mods,
          notes: it.notes
        });
      }
    });
    navigate(`/menu?branch=${lastOrder.branchId || ''}`);
  };
  return <main className="relative w-full min-h-screen flex flex-col bg-gray-900 font-sans overflow-hidden" data-id="element-1464">
      {/* Hero background */}
      <div className="absolute inset-0" data-id="element-1465">
        {general.coverUrl ? <img src={general.coverUrl} alt="" className="w-full h-full object-cover" data-id="element-1466" /> : <div className="w-full h-full bg-gray-800" data-id="element-1467" />}
        <div className="absolute inset-0 bg-black/40" data-id="element-1468" />
      </div>

      {/* Language & currency */}
      <div className="absolute top-4 right-4 z-20" data-id="element-1469">
        <button onClick={() => setPrefsOpen(v => !v)} className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-white/90 backdrop-blur text-gray-800 text-sm font-semibold shadow hover:bg-white transition-colors" aria-haspopup="true" aria-expanded={prefsOpen} data-id="element-1470">
          <Globe size={16} data-id="element-1471" />
          {general.language.toUpperCase()} · {general.currency}
        </button>
        <AnimatePresence data-id="element-1472">
          {prefsOpen && <motion.div initial={{
          opacity: 0,
          y: -8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-2xl p-4 space-y-3" data-id="element-1473">
              <div data-id="element-1474">
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-1475">
                  Language
                </label>
                <select value={general.language} onChange={e => updateGeneral({
              language: e.target.value as any
            })} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-100 focus:border-green-400" data-id="element-1476">
                  {LANGUAGES.map(l => <option key={l.value} value={l.value} data-id="element-1477">
                      {l.label}
                    </option>)}
                </select>
              </div>
              <div data-id="element-1478">
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-1479">
                  Currency
                </label>
                <select value={general.currency} onChange={e => updateGeneral({
              currency: e.target.value as any
            })} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-100 focus:border-green-400" data-id="element-1480">
                  {CURRENCIES.map(c => <option key={c.value} value={c.value} data-id="element-1481">
                      {c.label}
                    </option>)}
                </select>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Content */}
      <div className="relative flex-1 flex flex-col items-center justify-center px-5 py-10" data-id="element-1482">
        {/* Logo */}
        <div className="w-36 h-36 rounded-full bg-white shadow-xl flex items-center justify-center mb-8 overflow-hidden" data-id="element-1483">
          {general.logoUrl ? <img src={general.logoUrl} alt={`${general.businessName} logo`} className="w-full h-full object-cover" data-id="element-1484" /> : <span className="text-2xl font-extrabold text-green-700 px-3 text-center leading-tight line-clamp-3" data-id="element-1485">
              {general.businessName}
            </span>}
        </div>

        {/* Single-location open/closed status */}
        {locationList.length === 1 && <div className="mb-6 -mt-2 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur shadow" data-id="element-1486">
            <BranchStatusBadge branch={locationList[0]} data-id="element-1487" />
          </div>}

        {/* Returning-customer recognition */}
        {authed && <div className="w-full max-w-md mb-3 rounded-2xl bg-white/95 backdrop-blur shadow-lg p-4" data-id="element-1488">
            <div className="flex items-center justify-between gap-3" data-id="element-1489">
              <div className="min-w-0" data-id="element-1490">
                <p className="text-base font-bold text-gray-900 truncate" data-id="element-1491">
                  Welcome back, {greetingName} 👋
                </p>
                {lastOrder ? <p className="text-xs text-gray-500 truncate" data-id="element-1492">
                    Your last order ·{' '}
                    {lastOrder.items.reduce((n, it) => n + (it.quantity || 1), 0)}{' '}
                    item
                    {lastOrder.items.reduce((n, it) => n + (it.quantity || 1), 0) === 1 ? '' : 's'}
                  </p> : <p className="text-xs text-gray-500" data-id="element-1493">
                    Good to see you again.
                  </p>}
              </div>
              <button onClick={() => navigate('/orders')} className="flex items-center gap-1 text-sm font-semibold text-green-700 hover:text-green-800 transition-colors flex-shrink-0" data-id="element-1494">
                <ClipboardList size={16} data-id="element-1495" />
                History
              </button>
            </div>
            {lastOrder && <button onClick={reorderLast} className="mt-3 w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-green-600 text-white font-bold text-sm hover:bg-green-700 active:scale-[0.99] transition-all" data-id="element-1496">
                <RotateCcw size={18} data-id="element-1497" />
                Order again
              </button>}
          </div>}

        {/* Promotions */}
        <PromoBanner promos={activePromos} currency={general.currency} onOpen={() => startFlow('menu')} data-id="element-1498" />

        {/* Actions */}
        <div className="w-full max-w-md space-y-3" data-id="element-1499">
          <button onClick={() => startFlow('menu')} className="w-full flex items-center justify-center gap-3 py-4 rounded-xl bg-green-600 text-white font-bold text-lg shadow-lg hover:bg-green-700 active:scale-[0.99] transition-all" data-id="element-1500">
            <BookOpen size={22} data-id="element-1501" />
            View Menu
          </button>
          {reservationsAvailable && <button onClick={() => startFlow('reserve')} className="w-full flex items-center justify-center gap-3 py-4 rounded-xl bg-white/90 backdrop-blur text-gray-900 font-bold text-lg shadow-lg hover:bg-white active:scale-[0.99] transition-all" data-id="element-1502">
              <CalendarDays size={22} data-id="element-1503" />
              Reservations
            </button>}
          <button onClick={() => setSheet('contact')} className="w-full flex items-center justify-center gap-3 py-4 rounded-xl bg-white/90 backdrop-blur text-gray-900 font-bold text-lg shadow-lg hover:bg-white active:scale-[0.99] transition-all" data-id="element-1504">
            <Mail size={22} data-id="element-1505" />
            Contact Us
          </button>
          <button onClick={() => navigate('/leave-feedback')} className="w-full flex items-center justify-center gap-3 py-4 rounded-xl bg-white/90 backdrop-blur text-gray-900 font-bold text-lg shadow-lg hover:bg-white active:scale-[0.99] transition-all" data-id="element-1506">
            <MessageSquare size={22} data-id="element-1507" />
            Leave Feedback
          </button>
          {!authed && <button onClick={() => navigate('/login')} className="w-full flex items-center justify-center gap-3 py-4 rounded-xl border-2 border-white/80 text-white font-bold text-lg hover:bg-white/10 active:scale-[0.99] transition-all" data-id="element-1508">
              <LogIn size={22} data-id="element-1509" />
              Log in or Sign up
            </button>}
        </div>
      </div>

      {/* Footer */}
      <footer className="relative bg-white/90 backdrop-blur text-center py-4 px-4 text-xs text-gray-600 leading-relaxed" data-id="element-1510">
        © {new Date().getFullYear()} {general.businessName}. All rights
        reserved.
        <br data-id="element-1511" />
        Powered by <span className="font-bold text-gray-800" data-id="element-1512">Anbessa IT</span>
      </footer>

      {/* Sheets */}
      <AnimatePresence data-id="element-1513">
        {sheet === 'menu' && <LocationSheet title="Select Location" locations={locationList} lastLocationId={lastLocationId} onSelect={id => handleSelectLocation(id, 'menu')} onClose={() => setSheet(null)} data-id="element-1514" />}
        {sheet === 'reserve' && <LocationSheet title="Reserve a Table" locations={reservationLocations} lastLocationId={lastLocationId} onSelect={id => handleSelectLocation(id, 'reserve')} onClose={() => setSheet(null)} data-id="element-1515" />}
        {sheet === 'contact' && <ContactSheet businessName={general.businessName} phone={general.phone} email={general.email} address={locationList.length > 1 ? 'Multi Locations' : locationList[0]?.address || 'Multi Locations'} website={general.website} instagram={general.instagram} facebook={general.facebook} onClose={() => setSheet(null)} data-id="element-1516" />}
      </AnimatePresence>
    </main>;
}
function SheetShell({
  title,
  onClose,
  children
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return <div className="fixed inset-0 z-50 flex items-end justify-center" data-id="element-1517">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} onClick={onClose} className="absolute inset-0 bg-black/40" data-id="element-1518" />
      <motion.div initial={{
      y: '100%'
    }} animate={{
      y: 0
    }} exit={{
      y: '100%'
    }} transition={{
      type: 'spring',
      damping: 30,
      stiffness: 320
    }} className="relative w-full max-w-md bg-white rounded-t-3xl shadow-2xl max-h-[85vh] flex flex-col" data-id="element-1519">
        <div className="pt-3 pb-1 flex justify-center" data-id="element-1520">
          <span className="w-10 h-1.5 rounded-full bg-gray-300" data-id="element-1521" />
        </div>
        <div className="flex items-center justify-between px-6 py-3 border-b border-gray-100" data-id="element-1522">
          <h2 className="text-xl font-bold text-gray-900" data-id="element-1523">{title}</h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700 transition-colors" aria-label="Close" data-id="element-1524">
            <X size={24} data-id="element-1525" />
          </button>
        </div>
        <div className="overflow-y-auto" data-id="element-1526">{children}</div>
      </motion.div>
    </div>;
}
const METHOD_CHIPS: {
  key: keyof BranchOrderMethods;
  label: string;
}[] = [{
  key: 'dineIn',
  label: 'Dine-in'
}, {
  key: 'takeout',
  label: 'Takeout'
}, {
  key: 'delivery',
  label: 'Delivery'
}, {
  key: 'orderAhead',
  label: 'Order ahead'
}];
function LocationSheet({
  title,
  locations,
  lastLocationId,
  onSelect,
  onClose
}: {
  title: string;
  locations: Branch[];
  lastLocationId?: string | null;
  onSelect: (id: string) => void;
  onClose: () => void;
}) {
  return <SheetShell title={title} onClose={onClose} data-id="element-1527">
      {locations.length === 0 ? <div className="px-6 py-10 text-center text-sm text-gray-500" data-id="element-1528">
          No locations available right now.
        </div> : <ul data-id="element-1529">
          {locations.map(loc => {
        const chips = METHOD_CHIPS.filter(c => loc.methods?.[c.key]);
        const isLast = loc.id === lastLocationId;
        return <li key={loc.id} className="border-b border-gray-100" data-id="element-1530">
                <button onClick={() => onSelect(loc.id)} className="w-full text-left px-6 pt-4 pb-2 hover:bg-gray-50 transition-colors" data-id="element-1531">
                  <div className="flex items-center gap-2" data-id="element-1532">
                    <span className="font-bold text-gray-900 text-base" data-id="element-1533">
                      {loc.name}
                    </span>
                    {isLast && <span className="text-[10px] font-bold uppercase tracking-wide text-green-700 bg-green-100 rounded-full px-2 py-0.5" data-id="element-1534">
                        Last visited
                      </span>}
                  </div>
                  <div className="text-sm text-gray-400 mt-0.5" data-id="element-1535">
                    {loc.address || '—'}
                  </div>
                  <div className="mt-1.5" data-id="element-1536">
                    <BranchStatusBadge branch={loc} data-id="element-1537" />
                  </div>
                  {chips.length > 0 && <div className="flex flex-wrap gap-1.5 mt-2" data-id="element-1538">
                      {chips.map(c => <span key={c.key} className="text-[11px] font-semibold text-gray-600 bg-gray-100 rounded-full px-2 py-0.5" data-id="element-1539">
                          {c.label}
                        </span>)}
                    </div>}
                </button>
                {loc.address && <a href={mapsUrl(`${loc.name} ${loc.address}`)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-6 pb-3 text-sm font-semibold text-green-700 hover:text-green-800 transition-colors" data-id="element-1540">
                    <Navigation size={14} data-id="element-1541" />
                    Get directions
                  </a>}
              </li>;
      })}
        </ul>}
    </SheetShell>;
}
function ContactRow({
  icon,
  label,
  value
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return <div className="flex items-start gap-3" data-id="element-1542">
      <span className="text-green-600 mt-0.5 flex-shrink-0" data-id="element-1543">{icon}</span>
      <div data-id="element-1544">
        <div className="text-sm font-semibold text-gray-500" data-id="element-1545">{label}</div>
        <div className="text-lg text-gray-900" data-id="element-1546">{value}</div>
      </div>
    </div>;
}
function normalizeUrl(value: string, base?: string): string {
  const v = value.trim().replace(/^@/, '');
  if (/^https?:\/\//i.test(v)) return v;
  if (base) return `${base}${v}`;
  return `https://${v}`;
}
function ContactSheet({
  businessName,
  phone,
  email,
  address,
  website,
  instagram,
  facebook,
  onClose
}: {
  businessName: string;
  phone: string;
  email: string;
  address: string;
  website?: string;
  instagram?: string;
  facebook?: string;
  onClose: () => void;
}) {
  const socials = [website && {
    label: 'Website',
    href: normalizeUrl(website),
    icon: <Globe size={20} data-id="element-1547" />
  }, instagram && {
    label: 'Instagram',
    href: normalizeUrl(instagram, 'https://instagram.com/'),
    icon: <Instagram size={20} data-id="element-1548" />
  }, facebook && {
    label: 'Facebook',
    href: normalizeUrl(facebook, 'https://facebook.com/'),
    icon: <Facebook size={20} data-id="element-1549" />
  }].filter(Boolean) as {
    label: string;
    href: string;
    icon: React.ReactNode;
  }[];
  return <SheetShell title="Contact Us" onClose={onClose} data-id="element-1550">
      <div className="px-6 py-6" data-id="element-1551">
        <h3 className="text-center text-xl font-extrabold text-gray-900 mb-6" data-id="element-1552">
          {businessName}
        </h3>
        <div className="space-y-5" data-id="element-1553">
          <ContactRow icon={<Phone size={22} data-id="element-1555" />} label="Phone" value={phone} data-id="element-1554" />
          <ContactRow icon={<Mail size={22} data-id="element-1557" />} label="Email" value={email} data-id="element-1556" />
          <div data-id="element-1558">
            <ContactRow icon={<MapPin size={22} data-id="element-1560" />} label="Address" value={address} data-id="element-1559" />
            {address && address !== 'Multi Locations' && <a href={mapsUrl(`${businessName} ${address}`)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-1 ml-9 text-sm font-semibold text-green-700 hover:text-green-800 transition-colors" data-id="element-1561">
                <Navigation size={14} data-id="element-1562" />
                Get directions
              </a>}
          </div>
        </div>

        {socials.length > 0 && <div className="mt-6 pt-5 border-t border-gray-100" data-id="element-1563">
            <div className="text-sm font-semibold text-gray-500 mb-3" data-id="element-1564">
              Follow us
            </div>
            <div className="flex items-center gap-3" data-id="element-1565">
              {socials.map(s => <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label} title={s.label} className="w-11 h-11 rounded-full bg-gray-100 text-gray-700 flex items-center justify-center hover:bg-green-100 hover:text-green-700 transition-colors" data-id="element-1566">
                  {s.icon}
                </a>)}
            </div>
          </div>}

        <button onClick={onClose} className="w-full mt-8 py-4 rounded-xl bg-green-600 text-white font-bold text-lg hover:bg-green-700 transition-colors" data-id="element-1567">
          Close
        </button>
      </div>
    </SheetShell>;
}
const PROMO_DISMISS_KEY = 'mp_promo_banner_dismissed';
function loadDismissed(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(PROMO_DISMISS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}
function discountLabel(promo: Promotion, currency: string): string {
  return promo.type === 'percent' ? `${promo.value}% OFF` : `${currency} ${promo.value} OFF`;
}
function PromoBanner({
  promos,
  currency,
  onOpen
}: {
  promos: Promotion[];
  currency: string;
  onOpen: () => void;
}) {
  const [dismissed, setDismissed] = useState<string[]>(loadDismissed);
  const [index, setIndex] = useState(0);
  const visible = promos.filter(p => !dismissed.includes(p.id));
  // Keep the active index in range as promos appear/disappear.
  useEffect(() => {
    if (index >= visible.length) setIndex(0);
  }, [visible.length, index]);
  // Auto-rotate through multiple promos.
  useEffect(() => {
    if (visible.length <= 1) return;
    const id = window.setInterval(() => setIndex(i => (i + 1) % visible.length), 5000);
    return () => window.clearInterval(id);
  }, [visible.length]);
  if (visible.length === 0) return null;
  const promo = visible[Math.min(index, visible.length - 1)];
  const dismiss = (id: string) => {
    const next = [...dismissed, id];
    setDismissed(next);
    try {
      window.localStorage.setItem(PROMO_DISMISS_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
  };
  return <div className="w-full max-w-md mb-3" data-id="element-1568">
      <div className="relative rounded-2xl bg-white/95 backdrop-blur shadow-lg overflow-hidden" data-id="element-1569">
        <button onClick={e => {
        e.stopPropagation();
        dismiss(promo.id);
      }} className="absolute top-2 right-2 z-10 p-1 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors" aria-label="Dismiss promotion" data-id="element-1570">
          <X size={16} data-id="element-1571" />
        </button>
        <AnimatePresence mode="wait" data-id="element-1572">
          <motion.button key={promo.id} initial={{
          opacity: 0,
          x: 16
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -16
        }} transition={{
          duration: 0.25
        }} onClick={onOpen} className="w-full text-left p-4 pr-9 flex items-start gap-3" data-id="element-1573">
            <span className="w-11 h-11 rounded-xl bg-green-100 text-green-700 flex items-center justify-center flex-shrink-0" data-id="element-1574">
              <Tag size={20} data-id="element-1575" />
            </span>
            <div className="min-w-0 flex-1" data-id="element-1576">
              <div className="flex items-center gap-2 flex-wrap" data-id="element-1577">
                <span className="text-[11px] font-extrabold uppercase tracking-wide text-white bg-green-600 rounded-full px-2 py-0.5" data-id="element-1578">
                  {discountLabel(promo, currency)}
                </span>
                <span className="font-bold text-gray-900 text-sm truncate" data-id="element-1579">
                  {promo.title}
                </span>
              </div>
              {promo.description && <p className="text-xs text-gray-500 mt-1 line-clamp-2" data-id="element-1580">
                  {promo.description}
                </p>}
              <div className="flex items-center gap-2 mt-2" data-id="element-1581">
                <span className="text-[11px] font-mono font-bold text-green-700 bg-green-50 border border-green-200 rounded-md px-2 py-0.5" data-id="element-1582">
                  {promo.code}
                </span>
                <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-green-700" data-id="element-1583">
                  Order now <ChevronRight size={12} data-id="element-1584" />
                </span>
              </div>
            </div>
          </motion.button>
        </AnimatePresence>
      </div>
      {visible.length > 1 && <div className="flex items-center justify-center gap-1.5 mt-2" data-id="element-1585">
          {visible.map((p, i) => <button key={p.id} onClick={() => setIndex(i)} aria-label={`Show promotion ${i + 1}`} className={`h-1.5 rounded-full transition-all ${i === index ? 'w-5 bg-white' : 'w-1.5 bg-white/50'}`} data-id="element-1586" />)}
        </div>}
    </div>;
}